// MUNYA OS · § 18.5 + § 18.4 #15 · Menú preferencias usuario
// Toggle dark mode + focus mode + reactivar onboarding tour
//
// Focus mode unificado con keyboard shortcut "." (ShortcutsRoot.tsx)
//   - clase única: html.munya-focus-mode
//   - localStorage key única: mn:focus-mode

import { useEffect, useState, type ReactElement } from 'react';
import {
  Moon,
  Sun,
  EyeSlash,
  Eye,
  Gear,
  Lightbulb,
  X,
  Palette,
  Check,
} from '@phosphor-icons/react';
import { useFeatureFlag } from '../lib/feature-flags.js';
import { useDismissable } from '../lib/use-dismissable.js';

const STORAGE_KEY_DARK = 'munya:pref:dark';
const STORAGE_KEY_FOCUS = 'mn:focus-mode'; // ← coincide con ShortcutsRoot
const STORAGE_KEY_HEADER = 'munya:pref:header';

// Personalización del header · gama Munya. Cada opción lleva su par de contraste (fg) y el
// filtro del logo precalculados → legibilidad garantizada. Default: arena (#d2c7b6).
export interface HeaderOption {
  key: string;
  label: string;
  bg: string;
  fg: string;
  logo: string;
}
const DEFAULT_HEADER: HeaderOption = {
  key: 'sand',
  label: 'Arena',
  bg: '#d2c7b6',
  fg: '#362e2c',
  logo: 'brightness(0)',
};
export const HEADER_OPTIONS: HeaderOption[] = [
  DEFAULT_HEADER,
  { key: 'coffee', label: 'Café', bg: '#362e2c', fg: '#efeee9', logo: 'brightness(0) invert(1)' },
  { key: 'olive', label: 'Oliva', bg: '#7e8c69', fg: '#ffffff', logo: 'brightness(0) invert(1)' },
  { key: 'slate', label: 'Azulado', bg: '#6b7a8b', fg: '#ffffff', logo: 'brightness(0) invert(1)' },
  { key: 'brick', label: 'Teja', bg: '#5c3a2e', fg: '#ffffff', logo: 'brightness(0) invert(1)' },
  { key: 'cream', label: 'Crema', bg: '#efeee9', fg: '#362e2c', logo: 'brightness(0)' },
];

function applyHeader(key: string): void {
  const opt = HEADER_OPTIONS.find((o) => o.key === key) ?? DEFAULT_HEADER;
  const root = document.documentElement;
  root.style.setProperty('--mn-header-bg', opt.bg);
  root.style.setProperty('--mn-header-fg', opt.fg);
  root.style.setProperty('--mn-header-logo-filter', opt.logo);
}

export function useHeaderColor(): [string, (next: string) => void] {
  const [key, setKey] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_HEADER) ?? 'sand';
    } catch {
      return 'sand';
    }
  });
  useEffect(() => {
    applyHeader(key);
    try {
      localStorage.setItem(STORAGE_KEY_HEADER, key);
    } catch {
      /* ignore */
    }
  }, [key]);
  return [key, setKey];
}

function applyDark(enabled: boolean): void {
  document.documentElement.classList.toggle('munya-dark', enabled);
}

function applyFocus(enabled: boolean): void {
  document.documentElement.classList.toggle('munya-focus-mode', enabled);
}

export function useDarkMode(): [boolean, (next: boolean) => void] {
  const [dark, setDark] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_DARK) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    applyDark(dark);
    try {
      localStorage.setItem(STORAGE_KEY_DARK, String(dark));
    } catch {
      /* ignore */
    }
  }, [dark]);
  return [dark, setDark];
}

export function useFocusMode(): [boolean, (next: boolean) => void] {
  const [focus, setFocus] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_FOCUS) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    applyFocus(focus);
    try {
      localStorage.setItem(STORAGE_KEY_FOCUS, focus ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [focus]);
  // Sincroniza con cambios externos (atajo "." de ShortcutsRoot)
  useEffect(() => {
    const sync = (): void => {
      const cls = document.documentElement.classList.contains('munya-focus-mode');
      if (cls !== focus) setFocus(cls);
    };
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      obs.disconnect();
    };
  }, [focus]);
  return [focus, setFocus];
}

export function UserPreferencesMenu(): ReactElement {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useDarkMode();
  const [focus, setFocus] = useFocusMode();
  const [headerColor, setHeaderColor] = useHeaderColor();
  const darkModeEnabled = useFeatureFlag('dark_mode');

  // · #456 · el fondo solo cierra con el gesto completo (mousedown + mouseup sobre el fondo)
  const { backdropProps } = useDismissable({
    onDismiss: () => {
      setOpen(false);
    },
  });

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
        }}
        aria-label="Preferencias"
        className="mn-nav-inactive p-[0.625rem] lg:p-1.5 rounded transition"
      >
        <Gear size={16} weight="duotone" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-end p-4" {...backdropProps}>
          <div
            className="bg-[var(--color-munya-cream)] text-[var(--color-munya-coffee)] rounded-xl shadow-2xl w-72 max-lg:w-[18rem] mt-12 mr-2 max-h-[calc(100vh-5rem)] overflow-y-auto border border-[var(--color-munya-sand)]/60"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-munya-sand)]/60">
              <h3 className="text-sm font-semibold flex items-center gap-2 tracking-tight">
                <Gear size={14} weight="duotone" /> Preferencias
              </h3>
              <button
                onClick={() => {
                  setOpen(false);
                }}
                aria-label="Cerrar"
                className="opacity-50 hover:opacity-100"
              >
                <X size={14} />
              </button>
            </header>

            <div className="px-1 py-1.5">
              {darkModeEnabled && (
                <PreferenceRow
                  icon={
                    dark ? <Moon size={16} weight="duotone" /> : <Sun size={16} weight="duotone" />
                  }
                  title="Modo oscuro"
                  hint="Útil para trabajar de noche"
                  checked={dark}
                  onChange={setDark}
                />
              )}

              <PreferenceRow
                icon={
                  focus ? (
                    <EyeSlash size={16} weight="duotone" />
                  ) : (
                    <Eye size={16} weight="duotone" />
                  )
                }
                title="Modo concentración"
                hint="Oculta chat, notificaciones y KPIs · atajo «.»"
                checked={focus}
                onChange={setFocus}
              />
            </div>

            {/* Personalizar header · color de la barra superior dentro de la gama Munya */}
            <div className="border-t border-[var(--color-munya-sand)]/60 px-3 py-2.5">
              <p className="text-xs font-medium leading-tight flex items-center gap-2">
                <span className="text-[var(--color-munya-olive)] shrink-0">
                  <Palette size={16} weight="duotone" />
                </span>
                Personalizar header
              </p>
              <p className="text-[10px] opacity-55 leading-tight mt-0.5 mb-2 pl-[26px]">
                Color de la barra superior · gama Munya
              </p>
              <div className="flex flex-wrap gap-2 pl-[26px]">
                {HEADER_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      setHeaderColor(o.key);
                    }}
                    title={o.label}
                    aria-label={`Header ${o.label}`}
                    aria-pressed={headerColor === o.key}
                    className={`h-7 w-7 rounded-full flex items-center justify-center transition ${
                      headerColor === o.key
                        ? 'ring-2 ring-[var(--color-munya-olive)] ring-offset-1 ring-offset-[var(--color-munya-cream)]'
                        : 'border border-black/10 hover:scale-105'
                    }`}
                    style={{ background: o.bg }}
                  >
                    {headerColor === o.key && (
                      <Check size={13} weight="bold" style={{ color: o.fg }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-[var(--color-munya-sand)]/60 px-1 py-1.5">
              <button
                onClick={() => {
                  // #215 · el tour se persiste como "completado" al cerrarlo; para volver a verlo
                  // hay que resetear el estado en el backend (no un localStorage que no existía).
                  void fetch('/api/v1/onboarding/reset', {
                    method: 'POST',
                    credentials: 'include',
                  })
                    .catch(() => {
                      /* ignore */
                    })
                    .finally(() => {
                      setOpen(false);
                      window.location.reload();
                    });
                }}
                className="w-full text-left px-3 py-2 text-xs rounded-md hover:bg-[var(--color-munya-sand)]/30 transition flex items-center gap-2"
              >
                <Lightbulb size={13} weight="duotone" className="text-[var(--color-munya-olive)]" />
                Ver tour de bienvenida otra vez
              </button>
              {/* #299 · desactivar permanentemente el mensaje de bienvenida (no reaparece en nuevos accesos) */}
              <button
                onClick={() => {
                  void fetch('/api/v1/onboarding/dismiss', {
                    method: 'POST',
                    credentials: 'include',
                  })
                    .catch(() => {
                      /* ignore */
                    })
                    .finally(() => {
                      setOpen(false);
                    });
                }}
                className="w-full text-left px-3 py-2 text-xs rounded-md hover:bg-[var(--color-munya-sand)]/30 transition flex items-center gap-2"
              >
                <EyeSlash size={13} weight="duotone" className="text-[var(--color-munya-olive)]" />
                No volver a mostrar el tour
              </button>
            </div>

            <p className="text-[10px] opacity-50 px-4 py-2 border-t border-[var(--color-munya-sand)]/60">
              Las preferencias se guardan en este navegador.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function PreferenceRow({
  icon,
  title,
  hint,
  checked,
  onChange,
}: {
  icon: ReactElement;
  title: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => {
        onChange(!checked);
      }}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left rounded-md hover:bg-[var(--color-munya-sand)]/30 transition"
    >
      <span className="text-[var(--color-munya-olive)] shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium leading-tight">{title}</p>
        <p className="text-[10px] opacity-55 leading-tight mt-0.5">{hint}</p>
      </div>
      {/* Toggle switch estilizado · sustituye al checkbox <input> */}
      <span
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-[var(--color-munya-olive)]' : 'bg-[var(--color-munya-sand)]'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}
